import styled, { css } from 'styled-components';
import { FONT_WEIGHT, getHeadingFont, isFocused, spacingMixin, textShimmer } from '@belong/themes';
import { FontSizeHeadingKey, IHeading, THeadingLevel, VARIANT_TO_HEADING_LEVEL } from '@belong/types';
import { Text } from '../Text';

export const getAsPropFromVariant = ({ variant }: IHeading): THeadingLevel => {
  if (!variant || !VARIANT_TO_HEADING_LEVEL[variant]) {
    return VARIANT_TO_HEADING_LEVEL.large;
  }

  return VARIANT_TO_HEADING_LEVEL[variant];
};

/**
 * The `Heading` component is used to render headings.
 * Callers must omit attrs-backed props to use these defaults; explicitly passing `undefined` resets them.
 */
export const Heading = styled(Text).attrs<IHeading>(props => ({
  hasColor: props.hasColor,
  variant: props.variant || 'large',
  hasVisibleFocus: props.hasVisibleFocus,
  as: props.as || getAsPropFromVariant(props)
}))<IHeading>`
  ${spacingMixin}

  ${({ colourGradient }) => (colourGradient ? textShimmer : '')};

  color: ${({ colourGradient, hasColor, theme }) =>
    colourGradient
      ? // use the theme color when present or transparent for shimmer affected
        'transparent'
      : (hasColor ?? theme.foregroundColorDark)};

  text-transform: none;

  /* Visible focus outline for programmatic focus (a11y requirement) */
  ${({ hasVisibleFocus }) =>
    hasVisibleFocus &&
    css`
      &[tabindex='-1']:focus,
      &:focus-visible {
        ${isFocused()}
      }
    `}

  ${({ variant }) => variant && getHeadingFont(variant)}

  ${({ variant }) => {
    if (!variant) {
      return;
    }

    const semiBoldVariants: FontSizeHeadingKey[] = ['xsmall', 'small'];
    const isSemiBold = semiBoldVariants.includes(variant);

    return css`
      ${getHeadingFont(variant)}
      font-weight: ${isSemiBold ? FONT_WEIGHT.SEMI_BOLD : FONT_WEIGHT.BOLD};
    `;
  }};
`;
